import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ContactPage.css';

// Google Form configuration
const GOOGLE_FORM_CONFIG = {
  formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSdeUKNZdY5sf_nAWdnA-4nRCKJqvSYzxXnAMpRK6CODfWMRjw/formResponse',
  fields: {
    name: 'entry.1594865776',
    email: 'entry.1328583210',
    message: 'entry.1602432935'
  }
};

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

type SubmitStatus = 'idle' | 'success' | 'error';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    // Clear submit status when user modifies form
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = String(t('contact.errorName', 'Please enter your name'));
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = String(t('contact.errorEmail', 'Please enter your email'));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = String(t('contact.errorEmailInvalid', 'Please enter a valid email address'));
    }
    
    // Validate message
    if (!formData.message.trim()) {
      newErrors.message = String(t('contact.errorMessageEmpty', 'Please enter your message'));
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitToGoogleForm = async (data: FormData): Promise<void> => {
    const formBody = new URLSearchParams();
    formBody.append(GOOGLE_FORM_CONFIG.fields.name, data.name);
    formBody.append(GOOGLE_FORM_CONFIG.fields.email, data.email);
    formBody.append(GOOGLE_FORM_CONFIG.fields.message, data.message);

    // Using no-cors mode as Google Forms doesn't allow CORS
    // The response will be opaque, but the form submission will work
    await fetch(GOOGLE_FORM_CONFIG.formUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formBody.toString(),
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      await submitToGoogleForm(formData);
      
      // If we reach here, assume success (no-cors mode doesn't throw errors)
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setErrors({});
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: '📧',
      title: t('contact.emailContact'),
      details: ['synorlab.com@gmail.com']
    }
  ];

  return (
    <div className="contact-page">
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">{t('contact.title')}</h1>
          <p className="page-subtitle">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="contact-content">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-form-section">
              <h2 className="form-title">Send us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    {t('contact.name')} <span className="required-mark">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                    required
                  />
                  {errors.name && (
                    <div className="form-error">
                      <span className="error-icon">⚠</span>
                      {errors.name}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    {t('contact.email')} <span className="required-mark">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                    required
                  />
                  {errors.email && (
                    <div className="form-error">
                      <span className="error-icon">⚠</span>
                      {errors.email}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    {t('contact.message')} <span className="required-mark">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-textarea ${errors.message ? 'form-input-error' : ''}`}
                    rows={5}
                    required
                  />
                  {errors.message && (
                    <div className="form-error">
                      <span className="error-icon">⚠</span>
                      {errors.message}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className={`btn btn-primary btn-large ${isSubmitting ? 'loading' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact.sending', 'Sending...') : t('contact.send')}
                </button>

                {submitStatus === 'success' && (
                  <div className="form-success">
                    <span className="success-icon">✓</span>
                    {t('contact.successMessage', 'Thank you! We have received your message and will get back to you as soon as possible.')}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="form-error-message">
                    <span className="error-icon">⚠</span>
                    {t('contact.errorSubmit', 'Sorry, there was an error sending your message. Please try again or contact us directly at synorlab.com@gmail.com')}
                  </div>
                )}
              </form>
            </div>

            <div className="contact-info-section">
              <h2 className="info-title">Get in Touch</h2>
              <div className="contact-info-list">
                {contactInfo.map((info, index) => (
                  <div key={index} className="contact-info-item">
                    <div className="info-icon">{info.icon}</div>
                    <div className="info-content">
                      <h3 className="info-title">{info.title}</h3>
                      {info.details.map((detail, detailIndex) => (
                        <p key={detailIndex} className="info-detail">{detail}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;

